import { Box, Icon } from '@chakra-ui/react';
import { IconButton, Tab } from 'library/atoms';
import { TAG_VARIANTS } from 'library/atoms/Tag/Tag';
import TagStack from 'library/atoms/Tag/TagStack';
import { FilterIcon } from 'library/icons';
import DateTimePickerV2 from 'library/molecules/DateTimePickerV2';
import { useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { options } from 'views/main/organization/analytics-v3/constants';
import { useAnalyticsV3Context } from 'views/main/organization/analytics-v3/hooks/useAnalyticsV3Context';
import { ANALYTICS_V3_PATH } from 'views/main/routes/routes';
import { useShallow } from 'zustand/react/shallow';
import AnalyticsTab from './AnalyticsTab';
import NoiseReductionTab from './NoiseReductionTab';
import ResponseMetricsTab from './ResponseMetricsTab';
import { TopCardContainer } from './style';

import { useOrganizationConfig } from 'views/main/organization/schedules';
import momentTimeZone from 'moment-timezone';
import moment from 'moment';
import { AppTracker } from 'shared/analytics/tracker';
import { ANALYTIC_V3_EVENTS } from 'views/main/organization/analytics-v3/constants';
import {
  getDateWithTimeZoneOffset,
  prepareDateRangeForAPI,
} from 'views/main/organization/analytics-v3/helpers';
function TopCard({ isOrgAnalytics = false }: { isOrgAnalytics?: boolean }) {
  const isFilterDrawerOpen = useAnalyticsV3Context(state => state.isFilterDrawerOpen);
  const setIsFilterDrawerOpen = useAnalyticsV3Context(state => state.setIsFilterDrawerOpen);
  const tabIndex = useAnalyticsV3Context(state => state.tabIndex);
  const setTabIndex = useAnalyticsV3Context(state => state.setTabIndex);
  const filters = useAnalyticsV3Context(useShallow(state => state.filters));
  const setFilters = useAnalyticsV3Context(useShallow(state => state.setFilters));
  const dateRange = useAnalyticsV3Context(useShallow(state => state.dateRange));
  const setDateRange = useAnalyticsV3Context(useShallow(state => state.setDateRage));
  const tagKeys = filters?.tagKey;
  const tagValues = filters?.tagValue || [];
  const priorities = filters?.priority || [];
  const assignees = filters?.assignee;
  const serviceOwners = filters?.serviceOwner;
  const services = filters?.service || [];

  const selectedOwner = useMemo(
    () =>
      !serviceOwners?.meAndMySquads
        ? [
            ...(serviceOwners?.users ?? []),
            ...(serviceOwners?.squads ?? []),
            ...(serviceOwners?.myUsers ?? []),
            ...(serviceOwners?.mySquads ?? []),
          ]
        : [
            {
              label: 'You and Your Squads',
              value: 'meAndMySquads',
              username: '',
              meta: {
                filterType: 'serviceOwner',
              },
            },
            ...(serviceOwners?.users ?? []),
            ...(serviceOwners?.squads ?? []),
          ],
    [serviceOwners],
  );

  const selectedAssignees = useMemo(
    () =>
      !assignees?.meAndMySquads
        ? [
            ...(assignees?.users ?? []),
            ...(assignees?.squads ?? []),
            ...(assignees?.myUsers ?? []),
            ...(assignees?.mySquads ?? []),
          ]
        : [
            {
              label: 'You and Your Squads',
              value: 'meAndMySquads',
              username: '',
              meta: {
                filterType: 'assignee',
              },
            },
            ...(assignees?.users ?? []),
            ...(assignees?.squads ?? []),
          ],
    [assignees],
  );

  const appliedFilters = [
    ...(tagValues?.map(tag => ({
      label: `${tagKeys?.label} : ${tag.label}`,
      value: tag.value,
      meta: {
        filterType: 'tag',
      },
    })) ?? []),
    ...priorities.map(priority => ({
      label: `Priority : ${priority.label}`,
      value: priority.value,
      meta: {
        filterType: 'priority',
      },
    })),
    ...selectedAssignees.map(assignee => ({
      label: `Current Assignee : ${assignee.label} ${
        assignee.username ? `(${assignee?.username ?? ''})` : ''
      }`,
      value: assignee.value,
      meta: {
        filterType: 'assignee',
      },
    })),
    ...selectedOwner.map(owner => ({
      label: `Service Owner : ${owner.label} ${owner.username ? `(${owner?.username ?? ''})` : ''}`,
      value: owner.value,
      meta: {
        filterType: 'serviceOwner',
      },
    })),
    ...services.map(service => ({
      label: `Service : ${service.label}`,
      value: service.value,
      meta: {
        filterType: 'service',
      },
    })),
  ];

  const onFilter = () => {
    setIsFilterDrawerOpen(!isFilterDrawerOpen);
  };

  const { pathname } = useLocation();

  const currentOption = isOrgAnalytics
    ? {
        label: 'Organization Analytics',
        value: `${ANALYTICS_V3_PATH}/org-analytics`,
        icon: <Icon name="incident" />,
      }
    : options.find(option => option.value === pathname);

  const TAB_CONTENT = {
    1: <AnalyticsTab title="INCIDENTS" isOrgAnalytics={isOrgAnalytics} />,
    2: <ResponseMetricsTab isOrgAnalytics={isOrgAnalytics} />,
    3: <NoiseReductionTab title="NUMBER OF SILENCED ALERTS" isOrgAnalytics={isOrgAnalytics} />,
  };

  const handleDateFilterApply = (startDate: Date, endDate: Date, timeZone: string) => {
    const tabNameMap = {
      1: 'Incident Overview',
      2: 'Response Metrics',
      3: 'Alert Noise Reduction',
    };
    const { startDate: startDateUTC, endDate: endDateUTC } = prepareDateRangeForAPI({
      startDate,
      endDate,
      timeZone,
    });
    AppTracker.track(
      ANALYTIC_V3_EVENTS.INCIDENT_ANALYTICS_DATE_FILTER_APPLIED,
      {
        'Analytics Scope': isOrgAnalytics ? 'Organization Analytics' : 'Team Analytics',
        'Analytics Type': 'Incident Analytics',
        'Analytics Tab': tabNameMap[tabIndex as keyof typeof tabNameMap],
        'Start Date': startDateUTC,
        'End Date': endDateUTC,
        Timezone: timeZone,
      },
      true,
    );
    setDateRange({ startDate, endDate, timeZone });
  };

  const onClose = (index: number) => {
    const filter = appliedFilters[index];
    if (filter.meta?.filterType === 'priority') {
      setFilters({
        ...filters,
        priority: filters.priority.filter(p => p.value !== filter.value),
      });
    }
    if (filter.meta?.filterType === 'assignee') {
      if (filter.label.includes('You and Your Squads')) {
        setFilters({
          ...filters,
          assignee: {
            ...filters.assignee,
            meAndMySquads: false,
            myUsers: [],
            mySquads: [],
          },
        });
      } else {
        setFilters({
          ...filters,
          assignee: {
            ...filters.assignee,
            users: filters.assignee.users.filter(u => u.value !== filter.value),
            squads: filters.assignee.squads.filter(s => s.value !== filter.value),
          },
        });
      }
    }
    if (filter.meta?.filterType === 'serviceOwner') {
      if (filter.label.includes('You and Your Squads')) {
        setFilters({
          ...filters,
          serviceOwner: {
            ...filters.serviceOwner,
            meAndMySquads: false,
            myUsers: [],
            mySquads: [],
          },
        });
      } else {
        setFilters({
          ...filters,
          serviceOwner: {
            ...filters.serviceOwner,
            users: filters.serviceOwner.users.filter(u => u.value !== filter.value),
            squads: filters.serviceOwner.squads.filter(s => s.value !== filter.value),
          },
        });
      }
    }
    if (filter.meta?.filterType === 'service') {
      setFilters({
        ...filters,
        service: filters.service.filter(s => s.value !== filter.value),
      });
    }
    if (filter.meta?.filterType === 'tag') {
      const newTagValue = filters.tagValue.filter(t => t.value !== filter.value);
      setFilters({
        ...filters,
        tagValue: newTagValue,
        tagKey: newTagValue.length === 0 ? null : filters.tagKey,
      });
    }
  };

  const org = useOrganizationConfig();
  const userTimeZone = org.organization.currentUser.u?.time_zone ?? momentTimeZone.tz.guess();
  const selectedTimeZone = dateRange.timeZone;

  useEffect(() => {
    if (selectedTimeZone === '') {
      setDateRange({ ...dateRange, timeZone: userTimeZone });
    }
  }, [selectedTimeZone, userTimeZone]);

  return (
    <TopCardContainer>
      <div className="top-card-header">
        <div className="top-card-header-title" data-testid="top-card-header-title">
          {currentOption?.label}
        </div>
        <div className="cta-container">
          {!isOrgAnalytics && (
            <>
              <IconButton
                aria-label="Filter"
                variant="header"
                style={{ height: '36px', width: '36px' }}
                icon={<FilterIcon style={{ width: 16, height: 16, margin: 'auto' }} />}
                onClick={onFilter}
              />
              <div className="divider" />
            </>
          )}
          <DateTimePickerV2
            timezone={dateRange.timeZone}
            selectedDates={[dateRange.startDate, dateRange.endDate]}
            maxDate={moment().endOf('day').toDate()}
            onUpdateRange={() => {}}
            dateString=""
            onApply={handleDateFilterApply}
          />
        </div>
      </div>
      <div className="top-card-body-container">
        <p className="top-card-body">
          View and download reports of historical data on your{' '}
          {isOrgAnalytics ? 'organization' : 'team'}'s performance. Learn more about{' '}
          {isOrgAnalytics ? 'organization' : 'team'} analytics{' '}
          <a
            target="_blank"
            rel="noreferrer"
            style={{ color: '#0078FF' }}
            href={
              isOrgAnalytics
                ? 'https://support.squadcast.com/analytics/organization-level-analytics'
                : 'https://support.squadcast.com/analytics/analytics-new'
            }
          >
            here
          </a>
        </p>
        <Box mt={'3'}>
          <TagStack
            tags={appliedFilters}
            variant={TAG_VARIANTS.PLAIN}
            onClose={onClose}
            defaultLength={6}
          />
        </Box>
        <div className="top-card-body-tabs">
          <Tab
            isLazy
            pt="12px"
            index={tabIndex}
            onChange={setTabIndex}
            labels={[
              {
                name: 'Incident Overview',
              },
              {
                name: 'Response Metrics',
              },
              {
                name: 'Alert Noise Reduction',
              },
            ]}
            variant="outline"
          >
            <></>
          </Tab>
        </div>
        <div>{TAB_CONTENT[tabIndex as keyof typeof TAB_CONTENT]}</div>
      </div>
    </TopCardContainer>
  );
}

export default TopCard;
